// src/components/LAB/LABDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AthleteDashboard from '../AthleteDashboard/AthleteDashboard';
import LABAthleteView from './LABAthleteView';
import AthleteProfile from '../AthleteProfile/AthleteProfile';
import TrainingZones from '../TrainingZones/TrainingZones';
import PMCPane from '../TrainerDashboard/PMCPane';
import DashboardGroupsSidebar from '../TrainerDashboard/DashboardGroupsSidebar';
import LABImport from './LABImport';
import { useTranslation } from '../../i18n';
import API_BASE_URL from '../../config/api';

const API = (process.env.REACT_APP_API_URL || API_BASE_URL).replace(/\/+$/, '');
const DEFAULT_AVATAR = `${API}/uploads/athletes/default.jpg`;

function SafeImg({ src, alt, fallbackSrc, ...rest }) {
  const s = src == null ? '' : String(src).trim();
  const fb = fallbackSrc == null ? '' : String(fallbackSrc).trim();
  const finalSrc = s || fb;
  if (!finalSrc) return null;
  return <img src={finalSrc} alt={alt || ''} onError={e => { if (fb && e.currentTarget.src !== fb) e.currentTarget.src = fb; }} {...rest} />;
}

function resolvePhoto(photo) {
  if (!photo || photo === '/uploads/athletes/default.jpg') return null;
  if (photo.startsWith('data:') || photo.startsWith('http')) return photo;
  return `${API}${photo}`;
}

export default function LABDashboard() {
  const { language } = useTranslation();
  const [trainerId, setTrainerId] = useState(null);
  const [trainerProfile, setTrainerProfile] = useState({ firstName: 'Primer', lastName: 'Umbral', profilePicture: null });
  const [athletes, setAthletes] = useState([]);
  const [selectedAthleteId, setSelectedAthleteId] = useState(null);
  const [selectedAthleteInfo, setSelectedAthleteInfo] = useState(null);
  const [activeSection, setActiveSection] = useState('calendar');
  const [showImport, setShowImport] = useState(false);
  const [importKey, setImportKey] = useState(0);
  const [email, setEmail] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user') || localStorage.getItem('userData');
    let tid = null;
    try { tid = JSON.parse(userData)?._id || JSON.parse(userData)?.id; } catch(e) {}
    if (!tid) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        tid = payload._id || payload.id || payload.sub;
      } catch(e) {}
    }
    setTrainerId(tid);
  }, []);

  useEffect(() => {
    if (!trainerId) return;
    const token = localStorage.getItem('token');
    fetch(`${API}/api/trainers/profile/${trainerId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        const tr = data.trainer || data;
        const photo = tr.profilePhoto;
        setTrainerProfile({
          firstName: tr.firstName || 'Primer',
          lastName: tr.lastName || 'Umbral',
          profilePicture: photo || null,
        });
      }).catch(() => {});
  }, [trainerId]);

  useEffect(() => {
    if (!trainerId) return;
    const token = localStorage.getItem('token');
    fetch(`${API}/api/trainers/${trainerId}/athletes`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setAthletes(Array.isArray(data) ? data : (data.athletes || [])))
      .catch(() => {});
  }, [trainerId]);

  const handleSelectAthlete = useCallback((a) => {
    const fullName = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.name || 'Atleta';
    const photo = resolvePhoto(a.profilePhoto) || DEFAULT_AVATAR;
    const aid = String(a._id || a.id);
    setSelectedAthleteId(aid);
    setSelectedAthleteInfo({ name: fullName, photo });
    setActiveSection('calendar');
    // Guardar athleteId para que el calendario y AthleteProfile lo usen
    window.__labSelectedAthleteId = aid;
    localStorage.setItem('selectedAthleteId', aid);
  }, []);

  const handleSendInvite = () => {
    if (!email.trim()) return;
    const token = localStorage.getItem('token');
    fetch(`${API}/api/invitations/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ email, trainerId })
    }).then(() => setEmail('')).catch(() => {});
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#f8f9fa' }}>

      <header className="td-header">
        <div className="td-header-brand">
          {trainerProfile.profilePicture ? (
            <img src={trainerProfile.profilePicture} alt="avatar" className="td-header-avatar"
              onError={e => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff' }}>
              {trainerProfile.firstName?.charAt(0)}
            </div>
          )}
          <span className="td-header-name">{trainerProfile.firstName} {trainerProfile.lastName}</span>
        </div>
        <h1 className="td-header-title">Dashboard LAB</h1>
        <nav className="td-header-nav">
          <Link to="/trainer-profile" className="td-nav-btn">{language === 'en' ? 'View Profile' : 'Ver Perfil'}</Link>
          {selectedAthleteId && (
            <button type="button" className="td-nav-btn" onClick={() => setShowImport(true)}>
              {language === 'en' ? 'Import' : 'Importar'}
            </button>
          )}
          <button type="button" className="td-nav-btn td-nav-btn--danger"
            onClick={() => { localStorage.clear(); window.location.href = '/login'; }}>
            {language === 'en' ? 'Log out' : 'Cerrar sesión'}
          </button>
        </nav>
      </header>

      <div className="container-fluid mt-3" style={{ display:"grid", gridTemplateColumns:"216px minmax(0,1fr)", gap:12, alignItems:"start" }}>

        <aside className="td-left">
          <div className="td-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong>{language === 'en' ? `My Athletes (${athletes.length})` : `Mis Atletas (${athletes.length})`}</strong>
              {selectedAthleteId && (
                <button
                  style={{ fontSize: 11, padding: '4px 10px', borderRadius: 999, border: '1px solid #193255', background: '#193255', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                  onClick={() => { setSelectedAthleteId(null); setSelectedAthleteInfo(null); setActiveSection('calendar'); }}>
                  {language === 'en' ? 'Clear selection' : 'Limpiar selección'}
                </button>
              )}
            </div>
            <div className="td-ath-list">
              {athletes.map(a => {
                const fullName = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.name || 'Atleta';
                const photo = resolvePhoto(a.profilePhoto) || DEFAULT_AVATAR;
                const isSelected = String(selectedAthleteId) === String(a._id || a.id);
                return (
                  <div key={String(a._id || a.id)} className={`td-ath-item${isSelected ? ' active' : ''}`} onClick={() => handleSelectAthlete(a)}>
                    <SafeImg className="td-avatar" src={photo} fallbackSrc={DEFAULT_AVATAR} alt={fullName} />
                    <span>{fullName}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="td-groups">
            <div className="td-card td-groups-scroll">
              <DashboardGroupsSidebar trainerId={trainerId} athletes={athletes} onAddGroup={() => {}} />
            </div>
          </div>

          <div className="td-card" style={{ marginTop: 8 }}>
            <h6>{language === 'en' ? 'Invite athlete' : 'Invitar atleta'}</h6>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder={language === 'en' ? 'Email address' : 'Correo electrónico'}
              style={{ width: '100%', borderRadius: 999, border: '1px solid #e5e7eb', padding: '7px 12px', fontSize: 12, marginBottom: 8, boxSizing: 'border-box', outline: 'none' }} />
            <button
              style={{ width: '100%', borderRadius: 999, padding: '8px 0', background: '#f9c93c', border: 'none', color: '#193255', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}
              onClick={handleSendInvite}>
              {language === 'en' ? 'Send invitation' : 'Enviar invitación'}
            </button>
          </div>
        </aside>

        <main className="td-center-card">
          {!selectedAthleteId ? (
            <div className="td-card p-3">
              <h5 className="mb-1">Primer Umbral LAB</h5>
              <p className="text-muted mb-0">
                {language === 'en' ? 'Select an athlete from the sidebar.' : 'Selecciona un atleta del panel lateral.'}
              </p>
            </div>
          ) : (
            <div>
              {selectedAthleteInfo && (
                <div className="athlete-header" style={{ display: 'flex', alignItems: 'center', marginBottom: 12, padding: 10, background: '#f8fafc', borderRadius: 12 }}>
                  <SafeImg src={selectedAthleteInfo.photo} fallbackSrc={DEFAULT_AVATAR} alt={selectedAthleteInfo.name}
                    style={{ width: 60, height: 60, borderRadius: '50%', marginRight: 12, objectFit: 'cover' }} />
                  <h5 style={{ margin: 0 }}>
                    {language === 'en' ? `Viewing: ${selectedAthleteInfo.name}` : `Viendo a: ${selectedAthleteInfo.name}`}
                  </h5>
                </div>
              )}
              <LABAthleteView
                key={`lab-view-${selectedAthleteId}-${importKey}`}
                athleteId={selectedAthleteId}
                athleteName={selectedAthleteInfo?.name}
                athletePhoto={selectedAthleteInfo?.photo}
              />
            </div>
          )}
        </main>
      </div>

      {showImport && (
        <LABImport
          athleteId={selectedAthleteId}
          athleteName={selectedAthleteInfo?.name}
          onClose={() => setShowImport(false)}
          onSuccess={() => { setShowImport(false); setImportKey(k => k + 1); }}
        />
      )}
    </div>
  );
}
